import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert, Card, CardContent } from '@guildeducationinc/recess';
import { Tooltip } from '@mui/material';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { SAVE_CLIENT_FIELD_OF_STUDY, UPDATE_CLIENT_FIELD_OF_STUDY, } from '@src/graphql/queries/clientFieldOfStudy';
import { useClientFieldsOfStudy } from '@src/hooks/useClientFieldsOfStudy';
import { useClientIdParam } from '@src/hooks/useClientIdParam';
import { MinusCircleIcon, PlusCircleIcon } from 'lucide-react';
import { DataTable, SearchFilterBar, TrackableButton, useAlert, useLocale, useSessionState, } from 'pe-core-frontend';
export const ClientFieldsOfStudy = () => {
    const clientId = useClientIdParam() || 0;
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const [searchText, setSearchText] = useSessionState(`clientFieldsOfStudySearch-${clientId}`, '');
    const { clientFieldsOfStudy, loading, error, page, pageSize, setPage, setPageSize, total, orderBy, orderByDir, setOrderBy, setOrderByDir, refetch, } = useClientFieldsOfStudy({
        clientId,
        searchText,
    });
    const [save] = useMutation(SAVE_CLIENT_FIELD_OF_STUDY);
    const [update] = useMutation(UPDATE_CLIENT_FIELD_OF_STUDY);
    const handleMakeActive = async (fos, isActive) => {
        try {
            const result = await save({
                variables: {
                    input: {
                        id: fos.id,
                        isActive,
                    },
                },
            });
            if (result.data?.saveClientFieldOfStudy?.success) {
                await refetch();
                const message = result.data.saveClientFieldOfStudy.messages[0];
                const toShow = t(message.value, { values: message.params });
                showAlert(toShow, 'success');
            }
            else {
                const message = result.data.saveClientFieldOfStudy.messages[0];
                const toShow = t(message.value, { values: message.params });
                showAlert(toShow, 'error');
            }
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            throw new Error(t('Unable to save field of study'));
        }
    };
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const handleUpdateSearchResult = async (isActive) => {
        try {
            const result = await update({
                variables: {
                    input: {
                        id: null,
                        isActive,
                    },
                    spec: { clientId, name: searchText },
                },
            });
            if (result.data?.updateClientFieldOfStudy?.success) {
                await refetch();
                const message = result.data.updateClientFieldOfStudy.messages[0];
                const toShow = t(message.value, { values: message.params });
                showAlert(toShow, 'success');
            }
            else {
                const message = result.data.updateClientFieldOfStudy.messages[0];
                const toShow = t(message.value, { values: message.params });
                showAlert(toShow, 'error');
            }
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            throw new Error(t('Unable to update field of study'));
        }
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Fields of study'), searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, searchText: searchText, onSearchChange: setSearchText, searchPlaceholder: t('Search by Name or Category') }) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), total > 0 && !loading && (_jsx("div", { children: _jsxs("div", { className: 'mb-3 flex gap-1', children: [_jsx(TrackableButton, { buttonSize: 'small', appearance: 'outline', enText: 'Make Inactive', tag: `client_field_of_study_search`, onClick: () => {
                                                handleUpdateSearchResult(false);
                                            }, children: t('Make {count} Inactive', {
                                                values: { count: total },
                                            }) }), _jsx(TrackableButton, { buttonSize: 'small', appearance: 'outline', enText: 'Make Active', tag: `client_field_of_study_search`, onClick: () => {
                                                handleUpdateSearchResult(true);
                                            }, children: t('Make {count} Active', {
                                                values: { count: total },
                                            }) })] }) })), _jsx(DataTable, { disableRowSelectionOnClick: true, disableColumnMenu: true, rows: clientFieldsOfStudy, loading: loading, pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                    setPage(pagination.page + 1);
                                    setPageSize(pagination.pageSize);
                                }, onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'name', sort: orderByDir }], rowCount: total, columns: [
                                    {
                                        field: 'fieldOfStudy.name',
                                        headerName: t('Name'),
                                        flex: 0.4,
                                        renderCell: ({ row }) => row.fieldOfStudy.name,
                                    },
                                    {
                                        field: 'fieldOfStudy.category',
                                        headerName: t('Category'),
                                        flex: 0.3,
                                        renderCell: ({ row }) => row.fieldOfStudy.category,
                                    },
                                    {
                                        field: 'isActive',
                                        headerName: t('Active?'),
                                        width: 100,
                                        renderCell: params => (params.value ? t('Yes') : t('No')),
                                    },
                                    {
                                        field: 'actions',
                                        headerName: t('Actions'),
                                        width: 100,
                                        renderCell: ({ row }) => (_jsx("div", { children: _jsx(Tooltip, { title: row.isActive ? t('Make Inactive') : t('Make Active'), children: _jsx(TrackableButton, { btnType: 'icon', enText: row.isActive ? 'Make Inactive' : 'Make Active', tag: `client_field_of_study_${row.id}`, appearance: 'minimal', label: row.isActive ? t('Make Inactive') : t('Make Active'), icon: row.isActive ? (_jsx(MinusCircleIcon, { size: 20 })) : (_jsx(PlusCircleIcon, { size: 20 })), onClick: () => {
                                                        handleMakeActive(row, !row.isActive);
                                                    } }) }) })),
                                    },
                                ] })] }) }) })] }));
};
